(function () {
  'use strict';

  if (window.customElements.get('product-faq')) return;
  const MAX_QUESTIONS = 10;

  class ProductFaq extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.list = this.querySelector('[data-product-faq-list]');
      this.status = this.querySelector('[data-product-faq-status]');
      this.retry = this.querySelector('[data-product-faq-retry]');
      this.empty = this.querySelector('[data-product-faq-empty]');
      this.visited = this.visited || new Set();
      this.retry.addEventListener('click', () => this.loadAll(), { signal: this.controller.signal });
      this.loadAll();
    }

    disconnectedCallback() {
      if (this.controller) this.controller.abort();
      this.controller = null;
      this.loading = false;
    }

    async loadAll() {
      if (this.loading) return;
      const controller = this.controller;
      let nextUrl = this.dataset.nextUrl;
      let replaceInitialPage = Number(this.dataset.currentPage) > 1;
      if (replaceInitialPage) {
        const firstPage = new URL(window.location.href);
        firstPage.searchParams.set(this.dataset.pageParam, '1');
        nextUrl = firstPage.href;
      }
      if (!nextUrl) return;

      this.loading = true;
      this.retry.hidden = true;
      this.empty.hidden = true;
      this.status.hidden = false;
      this.status.textContent = 'Loading all questions...';
      this.setAttribute('aria-busy', 'true');

      try {
        while (nextUrl) {
          const url = new URL(nextUrl, window.location.href);
          if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) {
            throw new Error('Invalid product FAQ page URL.');
          }
          url.searchParams.set('section_id', this.dataset.sectionId);
          if (this.visited.has(url.href)) throw new Error('Repeated product FAQ page.');
          const response = await fetch(url.href, {
            signal: controller.signal,
            headers: { Accept: 'text/html' }
          });
          if (!response.ok) throw new Error('Product FAQ request failed.');
          const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
          const source = parsed.querySelector('product-faq');
          if (!source || source.dataset.productId !== this.dataset.productId || source.dataset.sectionId !== this.dataset.sectionId) {
            throw new Error('Product FAQs were not returned.');
          }
          if (controller.signal.aborted) return;

          if (replaceInitialPage) {
            this.list.replaceChildren();
            this.dataset.currentPage = '1';
            replaceInitialPage = false;
          }
          this.mergeQuestions(source);
          this.visited.add(url.href);
          nextUrl = source.dataset.nextUrl;
          this.dataset.nextUrl = nextUrl || '';
        }
        this.status.hidden = true;
        this.status.textContent = '';
        this.empty.hidden = this.list.children.length > 0;
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.status.textContent = 'Some questions could not be loaded. Please try again.';
        this.retry.hidden = false;
      } finally {
        if (this.controller === controller) {
          this.loading = false;
          this.removeAttribute('aria-busy');
        }
      }
    }

    mergeQuestions(source) {
      const ids = new Set(Array.from(this.list.children, (question) => question.dataset.faqArticleId));
      source.querySelectorAll('[data-faq-article-id]').forEach((incoming) => {
        if (ids.has(incoming.dataset.faqArticleId)) return;
        const following = Array.from(this.list.children).find((question) =>
          Number(question.dataset.faqPriority) > Number(incoming.dataset.faqPriority));
        this.list.insertBefore(incoming.cloneNode(true), following || null);
        ids.add(incoming.dataset.faqArticleId);
        // Later pages can replace a less specific question, but never grow the list past 10.
        while (this.list.children.length > MAX_QUESTIONS) this.list.lastElementChild.remove();
      });
    }
  }

  window.customElements.define('product-faq', ProductFaq);
})();
